import React, { useMemo } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Dialog con análisis ácido-base rico:
 * - Banner con el trastorno primario + compensatorio
 * - Escala de pH horizontal (real + sin compensar)
 * - Cards sin compensar → real
 * - Davenport dual-punto con isóbaras de PCO₂ (incluye la Winter esperada)
 * - Δ AG / Δ HCO₃ ratio explicado
 *
 * Recibe TODOS los grupos del visualizador (para poder rescatar Albúmina de
 * Nutricional/Hepático) y extrae de "Gases" los valores clave.
 */

// Buscar un exam por abbrev en TODOS los grupos.
const findByAbbrev = (groups, abbrev) => {
  if (!Array.isArray(groups)) return null;
  for (const g of groups) {
    for (const e of (g.exams || [])) {
      if (e.abbrev === abbrev) return e;
    }
  }
  return null;
};
const findByName = (groups, nameLower) => {
  if (!Array.isArray(groups)) return null;
  const target = String(nameLower).toLowerCase();
  for (const g of groups) {
    for (const e of (g.exams || [])) {
      if (String(e.name || "").toLowerCase() === target) return e;
    }
  }
  return null;
};
const val = (exam) => {
  if (!exam) return null;
  const v = exam.last_value;
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : v;
};

// pH sin compensar: aproximación Henderson-Hasselbalch con PCO₂ basal 40.
// pH = 6.10 + log10(HCO₃ / (0.03 × PCO₂)). Si trastorno primario es metabólico,
// mostramos qué pH tendríamos SIN compensación respiratoria (PCO₂ = 40).
const computeUncompensatedPh = (hco3, isPrimaryMetabolic) => {
  if (!Number.isFinite(hco3) || hco3 <= 0) return null;
  if (!isPrimaryMetabolic) return null;
  const ph = 6.10 + Math.log10(hco3 / (0.03 * 40));
  return Math.round(ph * 100) / 100;
};

const AcidBaseAnalysisDialog = ({ open, onClose, groups }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const data = useMemo(() => {
    const pha = val(findByAbbrev(groups, "pHa"));
    const phv = val(findByAbbrev(groups, "pHv"));
    const ph = pha ?? phv;
    const pco2a = val(findByAbbrev(groups, "pCO2a"));
    const pco2v = val(findByAbbrev(groups, "pCO2v"));
    const pco2 = pco2a ?? pco2v;
    // HCO₃ preferido: venoso primero (mismo criterio que backend), sino arterial.
    const hco3v = val(findByAbbrev(groups, "HCO3v"));
    const hco3a = val(findByAbbrev(groups, "HCO3a"));
    const hco3 = hco3v ?? hco3a;
    const ag = val(findByAbbrev(groups, "AG"));
    const agc = val(findByAbbrev(groups, "AGc"));
    const pco2Esp = val(findByAbbrev(groups, "pCO₂esp"));
    const hco3Esp = val(findByAbbrev(groups, "HCO₃esp"));
    const deltaRatio = val(findByAbbrev(groups, "Δ/Δ"));
    const dxAB = val(findByAbbrev(groups, "Dx AB"));
    const alb = val(findByName(groups, "Albumina")) ?? val(findByAbbrev(groups, "Alb"));
    const na = val(findByName(groups, "Sodio")) ?? val(findByAbbrev(groups, "Na"));
    const cl = val(findByName(groups, "Cloro")) ?? val(findByAbbrev(groups, "Cl"));

    // Determinar trastorno primario y compensatorio (mismo criterio que backend).
    let primary = null;
    let secondary = null;
    let isPrimaryMet = false;
    let isPrimaryResp = false;
    let mixedFlag = false;

    if (Number.isFinite(ph) && Number.isFinite(pco2) && Number.isFinite(hco3)) {
      // Trastorno primario por pH.
      if (ph < 7.35) {
        // Acidosis
        if (hco3 < 22 && pco2 <= 45) {
          primary = "Acidosis metabólica"; isPrimaryMet = true;
          secondary = pco2 < 35 ? "Compensación respiratoria (hiperventilación)" : "Sin compensación resp aún";
        } else if (pco2 > 45 && hco3 >= 22) {
          primary = "Acidosis respiratoria"; isPrimaryResp = true;
          secondary = hco3 > 26 ? "Compensación metabólica (crónica)" : "Aguda — sin compensación renal aún";
        } else if (hco3 < 22 && pco2 > 45) {
          primary = "Mixto: acidosis met + acidosis resp";
          secondary = "Ambos trastornos empujan hacia acidemia";
          mixedFlag = true; isPrimaryMet = true;
        }
      } else if (ph > 7.45) {
        // Alcalosis
        if (hco3 > 26 && pco2 >= 35) {
          primary = "Alcalosis metabólica"; isPrimaryMet = true;
          secondary = pco2 > 45 ? "Compensación respiratoria (hipoventilación)" : "Sin compensación resp completa";
        } else if (pco2 < 35 && hco3 <= 26) {
          primary = "Alcalosis respiratoria"; isPrimaryResp = true;
          secondary = hco3 < 22 ? "Compensación metabólica (crónica)" : "Aguda — sin compensación renal";
        } else if (hco3 > 26 && pco2 < 35) {
          primary = "Mixto: alcalosis met + alcalosis resp";
          secondary = "Ambos trastornos empujan hacia alcalemia";
          mixedFlag = true; isPrimaryMet = true;
        }
      } else {
        // pH normal — analizar por HCO₃/PCO₂
        if (hco3 < 22 && pco2 < 35) {
          primary = "Predomina acidosis metabólica";
          secondary = "Bien compensada por alcalosis respiratoria";
          isPrimaryMet = true;
        } else if (hco3 > 26 && pco2 > 45) {
          primary = "Predomina alcalosis metabólica";
          secondary = "Bien compensada por acidosis respiratoria";
          isPrimaryMet = true;
        } else if (hco3 < 22 || pco2 < 35 || hco3 > 26 || pco2 > 45) {
          primary = "Trastorno mixto compensado";
          secondary = "pH normal con parámetros alterados";
          mixedFlag = true;
        } else {
          primary = "Equilibrio normal";
          secondary = "Sin trastorno detectado";
        }
      }
    }

    const uncompPh = isPrimaryMet ? computeUncompensatedPh(hco3, true) : null;

    return {
      ph, pha, phv, pco2, pco2a, pco2v, hco3, hco3a, hco3v, ag, agc, pco2Esp,
      hco3Esp, deltaRatio, dxAB, alb, na, cl,
      primary, secondary, isPrimaryMet, isPrimaryResp, mixedFlag, uncompPh,
    };
  }, [groups]);

  // Coordenadas Davenport SVG (400 × 340).
  // pH 7.10 → x=45; pH 7.70 → x=380 (300 unidades para 0.60)
  // HCO₃ 0 → y=290; HCO₃ 48 → y=20 (270 unidades para 48)
  const xPh = (ph) => 45 + Math.max(0, Math.min(0.60, ph - 7.10)) / 0.60 * 335;
  const yHco3 = (hco3) => 290 - Math.max(0, Math.min(48, hco3)) / 48 * 270;

  const {
    ph, pco2, hco3, ag, agc, alb, na, cl, pco2Esp, deltaRatio, dxAB,
    primary, secondary, isPrimaryMet, uncompPh, mixedFlag,
  } = data;

  const noData = !Number.isFinite(ph) || !Number.isFinite(pco2) || !Number.isFinite(hco3);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      // Montado a nivel App (fuera del viewer). z-index EXTREMO con !important
      // para ganar sí o sí sobre el viewer (Dialog, z-index 1300) sin depender
      // del orden de inserción de estilos ni del ModalManager de MUI.
      sx={{ zIndex: "2147483000 !important" }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0,0,0,0.7)",
            backdropFilter: "blur(3px)",
          },
        },
      }}
      PaperProps={{
        sx: {
          bgcolor: "var(--bg-elevated)",
          color: "var(--text-secondary)",
          backgroundImage: "none",
          border: "1px solid rgba(var(--fg-rgb),0.08)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
          maxHeight: isMobile ? "100dvh" : "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid rgba(var(--fg-rgb),0.06)",
          py: 1.25,
        }}
      >
        <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 600 }}>
          Análisis ácido-base
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "var(--text-muted)" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {noData && (
          <Box sx={{ py: 4, textAlign: "center", color: "var(--text-muted)" }}>
            <Typography sx={{ fontSize: 14 }}>
              Sin datos suficientes de gasometría para el análisis.
            </Typography>
            <Typography sx={{ fontSize: 11, mt: 1 }}>
              Se requieren pH, PCO₂ y HCO₃ del mismo perfil.
            </Typography>
          </Box>
        )}

        {!noData && (
          <>
            {/* ─── Data strip ─── */}
            <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap", mb: 1.5 }}>
              {[
                ["pH", ph, ph < 7.35 ? "var(--color-danger)" : ph > 7.45 ? "var(--accent-sky)" : "var(--color-success)"],
                ["PCO₂", pco2, pco2 < 35 ? "var(--accent-sky)" : pco2 > 45 ? "var(--color-danger)" : "var(--color-success)"],
                ["HCO₃", hco3, hco3 < 22 ? "var(--accent-sky)" : hco3 > 26 ? "var(--color-danger)" : "var(--color-success)"],
                ...(Number.isFinite(ag) ? [["AG", ag, ag > 12 ? "var(--color-danger)" : "var(--color-success)"]] : []),
                ...(Number.isFinite(agc) ? [["AGc", agc, agc > 12 ? "var(--color-danger)" : "var(--color-success)"]] : []),
                ...(Number.isFinite(alb) ? [["Alb", alb, alb < 3.5 ? "var(--accent-sky)" : "var(--color-success)"]] : []),
                ...(Number.isFinite(na) ? [["Na", na, "var(--text-secondary)"]] : []),
                ...(Number.isFinite(cl) ? [["Cl", cl, "var(--text-secondary)"]] : []),
              ].map(([label, value, color]) => (
                <Box
                  key={label}
                  sx={{
                    px: 1,
                    py: 0.3,
                    borderRadius: "999px",
                    background: "rgba(var(--shadow-rgb),0.6)",
                    border: "1px solid rgba(var(--fg-rgb),0.08)",
                    fontSize: 10,
                    fontFamily: "'SF Mono', 'JetBrains Mono', monospace",
                    color: "var(--text-muted)",
                    display: "inline-flex",
                    gap: 0.6,
                  }}
                >
                  {label} <Box component="strong" sx={{ color, fontWeight: 700 }}>{value}</Box>
                </Box>
              ))}
            </Box>

            {/* ─── Banner verdict ─── */}
            <Box
              sx={{
                p: 1.5, mb: 1.5,
                borderRadius: 1.5,
                background: mixedFlag
                  ? "linear-gradient(135deg, rgba(248,113,113,0.12) 0%, rgba(130,177,255,0.12) 100%)"
                  : "linear-gradient(135deg, rgba(251,191,36,0.14) 0%, rgba(130,177,255,0.10) 100%)",
                border: `1px solid ${mixedFlag ? "rgba(248,113,113,0.30)" : "rgba(251,191,36,0.30)"}`,
              }}
            >
              <Typography sx={{ fontSize: 9, color: "var(--text-faint)", fontWeight: 700, letterSpacing: "0.6px", textTransform: "uppercase", mb: 0.5 }}>
                Diagnóstico
              </Typography>
              <Typography sx={{ fontSize: 17, fontWeight: 800, color: "var(--color-warning)", lineHeight: 1.2 }}>
                {primary || "—"}
              </Typography>
              {secondary && (
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "var(--accent-sky)", mt: 0.5 }}>
                  {secondary}
                </Typography>
              )}
              {Number.isFinite(pco2Esp) && isPrimaryMet && (
                <Typography sx={{ mt: 1, fontSize: 10.5, color: "var(--text-muted)", fontFamily: "'SF Mono', monospace", lineHeight: 1.4 }}>
                  Winter: PCO₂esp = <Box component="strong" sx={{ color: "var(--text-secondary)", fontWeight: 700 }}>{pco2Esp.toFixed(1)} ± 2</Box>
                  {" · real "}
                  <Box component="strong" sx={{ color: "var(--text-secondary)", fontWeight: 700 }}>{pco2.toFixed(1)}</Box>
                  {" · Δ "}{Math.abs(pco2 - pco2Esp).toFixed(1)}
                  {" → "}
                  <Box component="span" sx={{ color: Math.abs(pco2 - pco2Esp) <= 2.5 ? "var(--color-success)" : "var(--color-danger)", fontWeight: 700 }}>
                    {Math.abs(pco2 - pco2Esp) <= 2.5 ? "compensación adecuada" : "fuera de rango — mixto"}
                  </Box>
                </Typography>
              )}
              {dxAB && typeof dxAB === "string" && dxAB !== primary && (
                <Typography sx={{ mt: 1, fontSize: 10, color: "var(--text-faint)", fontStyle: "italic" }}>
                  Backend: {dxAB}
                </Typography>
              )}
            </Box>

            {/* ─── pH scale con markers uncomp + real ─── */}
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ position: "relative", height: 22, borderRadius: "999px",
                background: "linear-gradient(90deg, #f87171 0%, #fbbf24 30%, #4ade80 45%, #4ade80 55%, #82b1ff 70%, #508AA8 100%)",
                opacity: 0.85,
              }}>
                {Number.isFinite(uncompPh) && (
                  <Box sx={{ position: "absolute", top: -3, height: 28, width: 2, background: "var(--color-danger)", borderRadius: "2px", opacity: 0.75,
                    left: `${Math.max(0, Math.min(100, (uncompPh - 7.10) / 0.60 * 100))}%` }} />
                )}
                <Box sx={{ position: "absolute", top: -4, height: 30, width: 3, background: "var(--text-secondary)", borderRadius: "2px", boxShadow: "0 0 8px rgba(255,255,255,0.6)",
                  left: `${Math.max(0, Math.min(100, (ph - 7.10) / 0.60 * 100))}%` }} />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5, fontFamily: "'SF Mono', monospace", fontSize: 9, color: "var(--text-faint)" }}>
                <span>7.10</span>
                {Number.isFinite(uncompPh) && <span style={{ color: "var(--color-danger)" }}>▲ {uncompPh} sin comp</span>}
                <span style={{ color: "var(--text-secondary)", fontWeight: 700 }}>7.40</span>
                <span style={{ color: "var(--text-secondary)", fontWeight: 700 }}>▲ {ph.toFixed(2)} real</span>
                <span>7.70</span>
              </Box>
            </Box>

            {/* ─── Cards sin comp → real ─── */}
            {Number.isFinite(uncompPh) && (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 1, alignItems: "center", mb: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: 1.5, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.30)" }}>
                  <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", color: "var(--color-danger)" }}>
                    Sin compensar
                  </Typography>
                  <Typography sx={{ fontFamily: "'SF Mono', monospace", fontSize: 20, fontWeight: 800, color: "var(--color-danger)" }}>
                    {uncompPh}
                  </Typography>
                  <Typography sx={{ fontSize: 9.5, color: "var(--text-muted)" }}>
                    HCO₃ {hco3.toFixed(1)} · PCO₂ 40 (basal)
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", color: "var(--color-success)" }}>
                  <Box sx={{ fontSize: 18 }}>→</Box>
                  <Typography sx={{ fontSize: 8, letterSpacing: "0.5px", textTransform: "uppercase", fontWeight: 700, mt: 0.3 }}>
                    Comp resp
                  </Typography>
                </Box>
                <Box sx={{ p: 1, borderRadius: 1.5, background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.30)" }}>
                  <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", color: "var(--color-success)" }}>
                    Real
                  </Typography>
                  <Typography sx={{ fontFamily: "'SF Mono', monospace", fontSize: 20, fontWeight: 800, color: "var(--color-success)" }}>
                    {ph.toFixed(2)}
                  </Typography>
                  <Typography sx={{ fontSize: 9.5, color: "var(--text-muted)" }}>
                    PCO₂ {pco2.toFixed(1)} (compensada)
                  </Typography>
                </Box>
              </Box>
            )}

            {/* ─── Davenport SVG con dual-punto + isóbaras ─── */}
            <Box sx={{ mb: 1.5 }}>
              <svg viewBox="0 0 400 340" style={{ width: "100%", height: isMobile ? 260 : 320, background: "rgba(var(--shadow-rgb),0.6)", borderRadius: 10, border: "1px solid rgba(var(--fg-rgb),0.08)" }}>
                {/* Ejes */}
                <line x1="45" y1="290" x2="380" y2="290" stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
                <line x1="45" y1="290" x2="45" y2="20" stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>

                {/* Zonas */}
                <polygon points="45,165 220,165 220,290 45,290" fill="rgba(251,191,36,0.06)"/>
                <polygon points="220,20 380,20 380,165 220,165" fill="rgba(74,222,128,0.06)"/>
                <polygon points="45,20 220,20 220,165 45,165" fill="rgba(248,113,113,0.05)"/>
                <polygon points="220,165 380,165 380,290 220,290" fill="rgba(130,177,255,0.05)"/>

                {/* Labels de zona en ESQUINAS lejanas */}
                <text x="65" y="40" fill="#f87171" fontSize="10" fontWeight="700">Acidosis Resp</text>
                <text x="360" y="40" fill="#4ade80" fontSize="10" fontWeight="700" textAnchor="end">Alcalosis Met</text>
                <text x="65" y="280" fill="#fbbf24" fontSize="10" fontWeight="700">Acidosis Met</text>
                <text x="360" y="280" fill="#82b1ff" fontSize="10" fontWeight="700" textAnchor="end">Alcalosis Resp</text>

                {/* Isóbaras PCO2 */}
                <path d="M 45,140 Q 220,168 380,196" stroke="rgba(148,163,184,0.30)" strokeWidth="1.2" fill="none" strokeDasharray="3,3"/>
                <text x="382" y="200" fill="#94a3b8" fontSize="8">PCO₂ 40</text>
                <path d="M 45,88 Q 220,116 380,144" stroke="rgba(248,113,113,0.30)" strokeWidth="1" fill="none" strokeDasharray="3,3"/>
                <text x="382" y="148" fill="#f87171" fontSize="8">60</text>
                <path d="M 45,192 Q 220,220 380,248" stroke="rgba(130,177,255,0.30)" strokeWidth="1" fill="none" strokeDasharray="3,3"/>
                <text x="382" y="252" fill="#82b1ff" fontSize="8">20</text>

                {/* Isóbara Winter (verde) — solo si tenemos PCO₂ esperado */}
                {Number.isFinite(pco2Esp) && isPrimaryMet && (() => {
                  // Aproximación: la isóbara pasa por (pH-normal, HCO₃ que da PCO₂esp).
                  // Usamos los mismos offsets que las otras isóbaras.
                  // La isóbara de PCO₂esp está desplazada respecto a la de 40.
                  const offset = (pco2Esp - 40) * 1.5; // aproximación visual
                  return (
                    <>
                      <path
                        d={`M 45,${140 - offset} Q 220,${168 - offset} 380,${196 - offset}`}
                        stroke="#4ade80"
                        strokeWidth="2"
                        fill="none"
                        strokeDasharray="5,2"
                        opacity="0.85"
                      />
                      <text x="382" y={200 - offset + 4} fill="#4ade80" fontSize="8" fontWeight="700">{pco2Esp.toFixed(0)}esp</text>
                    </>
                  );
                })()}

                {/* Ejes labels */}
                <text x="215" y="315" fill="#94a3b8" fontSize="10" textAnchor="middle">pH</text>
                <text x="45" y="308" fill="#64748b" fontSize="9" textAnchor="middle">7.10</text>
                <text x="130" y="308" fill="#64748b" fontSize="9" textAnchor="middle">7.25</text>
                <text x="215" y="308" fill="#64748b" fontSize="9" textAnchor="middle">7.40</text>
                <text x="300" y="308" fill="#64748b" fontSize="9" textAnchor="middle">7.55</text>
                <text x="380" y="308" fill="#64748b" fontSize="9" textAnchor="middle">7.70</text>

                <text x="14" y="155" fill="#94a3b8" fontSize="10" textAnchor="middle" transform="rotate(-90, 14, 155)">HCO₃</text>
                <text x="38" y="294" fill="#64748b" fontSize="9" textAnchor="end">0</text>
                <text x="38" y="228" fill="#64748b" fontSize="9" textAnchor="end">12</text>
                <text x="38" y="165" fill="#64748b" fontSize="9" textAnchor="end">24</text>
                <text x="38" y="100" fill="#64748b" fontSize="9" textAnchor="end">36</text>
                <text x="38" y="35" fill="#64748b" fontSize="9" textAnchor="end">48</text>

                {/* Normal */}
                <circle cx="220" cy="165" r="3" fill="#94a3b8"/>
                <text x="226" y="160" fill="#94a3b8" fontSize="9">normal</text>

                {/* Punto sin compensar (solo si trastorno met) */}
                {Number.isFinite(uncompPh) && (() => {
                  const xu = xPh(uncompPh);
                  const yu = yHco3(hco3);
                  return (
                    <>
                      <line x1={xu} y1={yu} x2={xu} y2="290" stroke="rgba(248,113,113,0.30)" strokeWidth="1" strokeDasharray="2,2"/>
                      <line x1="45" y1={yu} x2={xu} y2={yu} stroke="rgba(248,113,113,0.30)" strokeWidth="1" strokeDasharray="2,2"/>
                      <circle cx={xu} cy={yu} r="6" fill="rgba(248,113,113,0.15)" stroke="#f87171" strokeWidth="2"/>
                      <text x={xu} y={yu - 12} fill="#f87171" fontSize="9" fontWeight="700" textAnchor="middle">sin comp</text>
                      <text x={xu} y={yu + 22} fill="#f87171" fontSize="8" opacity="0.85" textAnchor="middle" fontFamily="monospace">{uncompPh} · {hco3.toFixed(1)}</text>

                      {/* Flecha compensatoria hasta el punto real */}
                      <defs>
                        <marker id="arrGreen" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
                          <path d="M0,0 L6,4 L0,8 Z" fill="#4ade80"/>
                        </marker>
                      </defs>
                      <line x1={xu + 8} y1={yu} x2={xPh(ph) - 15} y2={yu} stroke="#4ade80" strokeWidth="2" strokeDasharray="5,3" markerEnd="url(#arrGreen)" opacity="0.75"/>
                      <text x={(xu + xPh(ph)) / 2} y={yu - 6} fill="#4ade80" fontSize="9" fontWeight="700" textAnchor="middle">compensación resp</text>
                    </>
                  );
                })()}

                {/* Punto REAL */}
                {(() => {
                  const xr = xPh(ph);
                  const yr = yHco3(hco3);
                  return (
                    <>
                      <line x1={xr} y1={yr} x2={xr} y2="290" stroke="rgba(251,191,36,0.30)" strokeWidth="1" strokeDasharray="2,2"/>
                      <line x1="45" y1={yr} x2={xr} y2={yr} stroke="rgba(251,191,36,0.30)" strokeWidth="1" strokeDasharray="2,2"/>
                      <circle cx={xr} cy={yr} r="10" fill="none" stroke="#fbbf24" strokeWidth="2" opacity="0.4">
                        <animate attributeName="r" values="10;16;10" dur="2s" repeatCount="indefinite"/>
                        <animate attributeName="opacity" values="0.4;0.05;0.4" dur="2s" repeatCount="indefinite"/>
                      </circle>
                      <circle cx={xr} cy={yr} r="7" fill="#fbbf24"/>
                      {/* Badge Real desplazado a la derecha */}
                      <rect
                        x={xr + 13}
                        y={yr - 12}
                        width="82"
                        height="26"
                        rx="4"
                        fill="rgba(var(--bg-elevated-rgb),0.85)"
                        stroke="rgba(251,191,36,0.30)"
                      />
                      <text x={xr + 54} y={yr + 1} fill="#fff" fontSize="10" fontWeight="700" textAnchor="middle">Real {ph.toFixed(2)}</text>
                      <text x={xr + 54} y={yr + 11} fill="#fbbf24" fontSize="8" textAnchor="middle" fontFamily="monospace">HCO₃ {hco3.toFixed(1)}</text>
                    </>
                  );
                })()}
              </svg>
            </Box>

            {/* ─── Δ AG / Δ HCO₃ ratio ─── */}
            {Number.isFinite(deltaRatio) && (
              <Box sx={{ p: 1.2, mb: 1.5, borderRadius: 1.5, background: "rgba(var(--shadow-rgb),0.6)", border: "1px solid rgba(var(--fg-rgb),0.08)" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography sx={{ fontSize: 9, color: "var(--text-faint)", fontWeight: 700, letterSpacing: "0.6px", textTransform: "uppercase" }}>
                    Δ AG / Δ HCO₃
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: "var(--text-faint)" }}>
                    {deltaRatio < 1 ? "hiperclorémica" : deltaRatio <= 2 ? "AG↑ pura" : "+ alc met (o resp cr)"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography sx={{ fontFamily: "'SF Mono', monospace", fontSize: 22, fontWeight: 800, color: "var(--color-warning)" }}>
                    {deltaRatio.toFixed(2)}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: "var(--text-muted)", lineHeight: 1.4, flex: 1 }}>
                    (AG−12) / (24−HCO₃) = ({(ag || 0).toFixed(1)}−12) / (24−{hco3.toFixed(1)}).
                    {" "}
                    {deltaRatio < 1 && "Sugiere acidosis met hiperclorémica pura."}
                    {deltaRatio >= 1 && deltaRatio <= 2 && "Sugiere acidosis met con AG↑ pura."}
                    {deltaRatio > 2 && "Sugiere AG↑ + alcalosis metabólica sobreagregada."}
                    {Number.isFinite(agc) && agc > 12 && (agc - (ag || 0)) > 2 && " AGc alto por hipoalbuminemia — buscar AG↑ oculta."}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* ─── Fórmulas cheat sheet ─── */}
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: 9, color: "var(--text-faint)", fontWeight: 700, letterSpacing: "0.6px", textTransform: "uppercase", mb: 0.6 }}>
                Fórmulas
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 0.6, fontSize: 10, fontFamily: "'SF Mono', monospace", color: "var(--text-muted)" }}>
                <Box sx={{ p: 0.8, borderRadius: 1, background: "rgba(var(--shadow-rgb),0.5)", border: "1px solid rgba(var(--fg-rgb),0.06)" }}>
                  <strong style={{ color: "var(--color-warning)" }}>Ac met · Winter:</strong> PCO₂ = 1.5×HCO₃ + 8 ± 2
                </Box>
                <Box sx={{ p: 0.8, borderRadius: 1, background: "rgba(var(--shadow-rgb),0.5)", border: "1px solid rgba(var(--fg-rgb),0.06)" }}>
                  <strong style={{ color: "var(--color-success)" }}>Alc met:</strong> PCO₂ = 0.7×HCO₃ + 21
                </Box>
                <Box sx={{ p: 0.8, borderRadius: 1, background: "rgba(var(--shadow-rgb),0.5)", border: "1px solid rgba(var(--fg-rgb),0.06)" }}>
                  <strong style={{ color: "var(--color-danger)" }}>Ac resp aguda:</strong> HCO₃ = 24 + (PCO₂−40)×0.1
                </Box>
                <Box sx={{ p: 0.8, borderRadius: 1, background: "rgba(var(--shadow-rgb),0.5)", border: "1px solid rgba(var(--fg-rgb),0.06)" }}>
                  <strong style={{ color: "var(--color-danger)" }}>Ac resp crónica:</strong> HCO₃ = 24 + (PCO₂−40)×0.35
                </Box>
                <Box sx={{ p: 0.8, borderRadius: 1, background: "rgba(var(--shadow-rgb),0.5)", border: "1px solid rgba(var(--fg-rgb),0.06)" }}>
                  <strong style={{ color: "var(--accent-sky)" }}>Alc resp aguda:</strong> HCO₃ = 24 − (40−PCO₂)×0.2
                </Box>
                <Box sx={{ p: 0.8, borderRadius: 1, background: "rgba(var(--shadow-rgb),0.5)", border: "1px solid rgba(var(--fg-rgb),0.06)" }}>
                  <strong style={{ color: "var(--accent-sky)" }}>Alc resp crónica:</strong> HCO₃ = 24 − (40−PCO₂)×0.5
                </Box>
                <Box sx={{ p: 0.8, borderRadius: 1, background: "rgba(var(--shadow-rgb),0.5)", border: "1px solid rgba(var(--fg-rgb),0.06)", gridColumn: { xs: "1", sm: "1 / span 2" } }}>
                  <strong style={{ color: "var(--accent-info)" }}>AG:</strong> Na − (Cl + HCO₃) · normal 10 ± 2  ·  <strong style={{ color: "var(--accent-info)" }}>AGc:</strong> AG + 2.5×(4 − Alb)
                </Box>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AcidBaseAnalysisDialog;
